import { Modal, Select } from 'ant-design-vue'
import { createVNode, h } from 'vue'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
import { checkCrossPoolCall } from '@/api/UserPool/activeRevisit'

const CROSS_POOL_TIP = '该患者已在其他健管的回访池中，为避免重复联系，请慎重考虑。确认拨打电话？'
const DOCTOR_SELECT_TIP = '请选择本次通话事项对应的医生'
const DOCTOR_SELECT_PLACEHOLDER = '请选择患者已绑定的医生'

/**
 * 拨打患者电话前的校验守卫
 *
 * 接口 /active-followup/check-cross-pool-call 用 promptType 统一决定弹哪种提示，文案只取后端 message：
 *  - 0：无提示，直接走 onPass
 *  - 1：跨回访池确认（等价旧 needConfirm=true），不查绑定医生 → onContinue(跨池埋点)
 *  - 2：自动加入回访池确认；bindDoctorType=2（多个医生）时先让用户选定本次通话对应的医生
 *       → onContinue({ ucDoctorId })
 * 两种提示互斥；接口本身不创建回访任务。
 *
 * @param {Object} options
 * @param {String|Number} options.ucPatientId 患者id（预建档患者传 prePatientId），为空时直接走原流程
 * @param {String|Number} [options.ucDoctorId] 已确定医生时才传（医生池 / 患者档案下方为一对一）
 * @param {String} options.source             拨打来源页面标识（userPool/patientFile/patientFilePhone/callLog/activeRevisit），
 *                                            确认跨池拨打后作为 sourcePage 随拨打参数上送
 * @param {Function} options.onPass           无需提示时执行，保持原拨打流程
 * @param {Function} [options.onContinue]     已确认（跨池确认 / 自动加入确认）后执行，入参为附加拨打参数
 *                                            { sourcePage, crossPoolConfirmed, crossPoolTargetFollowupId } 或 { ucDoctorId }，
 *                                            实现方需合并进拨打参数；缺省兜底为 onPass
 * @param {Boolean} [options.skipAutoJoinPrompt] 跳过 promptType=2 的「自动加入回访池」提示（如患者档案-关联手机号）
 */
export default function useRevisitPoolGuard(options) {
  const {
    ucPatientId,
    ucDoctorId,
    source,
    onPass,
    onContinue,
    skipAutoJoinPrompt = false
  } = options
  const goOn = onContinue || onPass
  // 非患者场景不应传入；缺少患者标识无法校验，直接走原拨打流程
  if (!ucPatientId || typeof onPass !== 'function') {
    onPass && onPass()
    return
  }
  checkCrossPoolCall({ ucPatientId, ucDoctorId })
    .then(result => {
      const data = result || {}
      // promptType 缺失时兼容旧后端：needConfirm 等价 promptType=1
      const rawPromptType = Number(data.promptType)
      const promptType = Number.isFinite(rawPromptType) ? rawPromptType : data.needConfirm ? 1 : 0
      const doctors = Array.isArray(data.doctors) ? data.doctors : []

      // promptType=1：跨回访池确认，只走原提醒，不看绑定医生
      if (promptType === 1) {
        Modal.confirm({
          centered: true,
          title: '提示',
          icon: createVNode(ExclamationCircleOutlined),
          // 文案直接用接口返回的 message
          content: data.message || CROSS_POOL_TIP,
          okText: '继续拨打',
          cancelText: '取消拨打',
          onOk: () => {
            // 埋点随拨打参数透传至 record-save-before-call：来源页面、已确认跨池拨打、目标主动回访任务ID
            goOn({
              sourcePage: source,
              crossPoolConfirmed: true,
              crossPoolTargetFollowupId: data.targetFollowupId
            })
          }
        })
        return
      }

      // promptType=2：自动加入回访池确认；关联多个医生时先选医生
      if (promptType === 2 && !skipAutoJoinPrompt) {
        openAutoJoinConfirm({
          message: data.message,
          patientName: data.patientName,
          doctors,
          needSelect: data.bindDoctorType === 2 || doctors.length > 1,
          onConfirm: extra => goOn(extra)
        })
        return
      }

      // promptType=0（或跳过提示）：不拦截，走原拨打流程
      onPass()
    })
    .catch(() => {
      // 检查接口异常不阻断拨打，降级走原流程
      onPass()
    })
}

/**
 * promptType=2 的「自动加入回访池」确认弹窗
 * - needSelect=true：下拉选择本次通话对应的医生，选中前「拨打」置灰
 * - needSelect=false：一对一 / 无绑定医生，直接确认
 */
function openAutoJoinConfirm({ message, patientName, doctors, needSelect, onConfirm }) {
  // 下拉只展示医生姓名，其余字段带着方便后续扩展
  const options = doctors.map(item => ({
    value: item.ucDoctorId,
    label: item.doctorName,
    raw: item
  }))
  let selectedDoctorId = null
  let modal = null

  const content = () => {
    const nodes = [
      // 后端 message 作为正文，放在最上面
      h(
        'div',
        { style: 'color: rgba(0, 0, 0, 0.85); font-size: 14px; line-height: 22px;' },
        message || `确定给患者-${patientName || ''}拨打电话吗？`
      )
    ]
    if (needSelect) {
      // 下拉上方的黄色提示
      nodes.push(
        h(
          'div',
          {
            style:
              'margin: 16px 0 12px; padding: 8px 12px; border-radius: 4px; background: #fff7ee; color: #fc8a0f; font-size: 14px; font-weight: 500; line-height: 20px;'
          },
          DOCTOR_SELECT_TIP
        )
      )
      nodes.push(
        h(Select, {
          options,
          placeholder: DOCTOR_SELECT_PLACEHOLDER,
          allowClear: true,
          showSearch: true,
          optionFilterProp: 'label',
          style: 'width: 100%',
          'onUpdate:value': val => {
            selectedDoctorId = val
            // 选中医生后「拨打」按钮才高亮
            modal?.update({ okButtonProps: { disabled: !val } })
          }
        })
      )
    }
    return h('div', nodes)
  }

  modal = Modal.confirm({
    centered: true,
    title: '提示',
    icon: createVNode(ExclamationCircleOutlined),
    width: 480,
    content,
    okText: '拨打',
    cancelText: '取消',
    // 需要选医生时，未选择前不可拨打
    okButtonProps: { disabled: !!needSelect },
    onOk: () => {
      if (needSelect) {
        if (!selectedDoctorId) return Promise.reject()
        onConfirm({ ucDoctorId: selectedDoctorId })
        return
      }
      // 一对一：把该医生一并带上，明确本次医患关系
      const only = doctors.length === 1 ? doctors[0] : null
      onConfirm(only?.ucDoctorId ? { ucDoctorId: only.ucDoctorId } : undefined)
    }
  })
}
